import React, { useCallback, useMemo } from 'react';
import { SectionList, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useAppSelector } from 'store/hooks';
import { AppView, CardView, Spacer } from 'design-system';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { IconEmma } from 'design-system/icons';
import SectionHeader from 'design-system/SectionHeader';
import Squircle from 'design-system/Squircle';
import Text from 'design-system/Text';
import { UpgradeWebModal } from 'design-system/UpgradeWebModal';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import { useAmplitudeOnMountEvent } from 'features/invest/utils/analytics';
import { Tier } from 'features/premium/entities';
import { selectUserTier } from 'features/premium/selectors/tier';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount, getTierText } from 'utils/formatting';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { OnboardingFlow } from 'utils/types/navigation';
import { showUnavailableOnWeb } from 'utils/webGate';
import { isWeb } from '../../../constants';
import { selectPotProductFromProductId } from '../reducers/selectors';
import { getPotEventName } from '../utils';
const strings = {
    yourPlanSectionTitle: 'For your plan',
    upgradeSectionTitle: 'Upgrade to get more interest',
};
const AnimatedSectionList = Animated.createAnimatedComponent(SectionList);
export const PotInterestRatesScreen = withAnimatedNavigationHeader(({ route, scrollHandler, navigation }) => {
    const productId = route.params?.productId;
    const activeTier = useAppSelector(selectUserTier);
    const interestPotProductsTierRateMap = useAppSelector((store) => store.pots.interestPotProductsTierRateMap);
    const product = useAppSelector((store) => selectPotProductFromProductId(store, productId));
    const colors = useColors();
    const styles = useStyles(styleSet);
    useAmplitudeOnMountEvent(OnboardingFlow.Pots, 'Pot Interest Screen', {
        product: getPotEventName(product?.type),
    });
    const handleContinue = useCallback((tier) => () => {
        if (product) {
            if (tier === activeTier) {
                if (showUnavailableOnWeb('emma://save?tab=pots', 'Create a Pot')) {
                    return;
                }
                navigation.navigateDeprecated('CreatePot', { productId });
            }
            else {
                if (isWeb) {
                    UpgradeWebModal.show(tier);
                    return;
                }
                navigation.navigateDeprecated('EmmaProModal', {
                    tier: tier,
                    benefitId: 'potsInterest',
                });
            }
        }
    }, [activeTier, product, productId]);
    const renderIcon = useCallback((tier) => {
        switch (tier) {
            case Tier.ultimate:
                return <IconEmma gradientKey="ultimate"/>;
            case Tier.pro:
                return <IconEmma gradientKey="pro"/>;
            case Tier.plus:
                return <IconEmma gradientKey="plus"/>;
            default:
                return <IconEmma colorKey="secondary"/>;
        }
    }, []);
    const sections = useMemo(() => {
        const data = [];
        if (activeTier) {
            data.push({
                title: strings.yourPlanSectionTitle,
                data: [
                    {
                        tier: activeTier,
                    },
                ],
            });
        }
        switch (activeTier) {
            // Need to handle ultimate because the tier might change when the user is in this screen
            case Tier.ultimate: {
                break;
            }
            case Tier.pro: {
                data.push({
                    title: strings.upgradeSectionTitle,
                    data: [
                        {
                            tier: Tier.ultimate,
                        },
                    ],
                });
                break;
            }
            case Tier.plus: {
                data.push({
                    title: strings.upgradeSectionTitle,
                    data: [
                        {
                            tier: Tier.pro,
                        },
                        {
                            tier: Tier.ultimate,
                        },
                    ],
                });
                break;
            }
            default:
                data.push({
                    title: strings.upgradeSectionTitle,
                    data: [
                        {
                            tier: Tier.plus,
                        },
                        {
                            tier: Tier.pro,
                        },
                        {
                            tier: Tier.ultimate,
                        },
                    ],
                });
                break;
        }
        return data;
    }, [activeTier]);
    const renderSectionTitle = useCallback(({ section: { title } }) => (<SectionHeader verticalMargins horizontalMargins>
          {title}
        </SectionHeader>), []);
    const renderItem = useCallback(({ item }) => (<TouchableBounce onPress={handleContinue(item.tier)}>
          <CardView withPadding style={styles.renderItem}>
            <View style={styles.row}>
              <Squircle color={colors.background.dark}>{renderIcon(item.tier)}</Squircle>
              <View style={styles.flex1}/>
              <View style={styles.tierContainer}>
                <Text Text-14 Secondary style={styles.tierText}>
                  {getTierText(item.tier)}
                </Text>
              </View>
            </View>
            <Spacer h={8}/>
            {!!interestPotProductsTierRateMap?.[productId]?.[item.tier] && (<Text Text-16>
                GBP - <Text Numbers-16>{interestPotProductsTierRateMap?.[productId]?.[item.tier]}%</Text> AER
              </Text>)}
            {Boolean(interestPotProductsTierRateMap?.[productId]) && (<>
                <Spacer h={4}/>
                <Text TextThin-14 Secondary>
                  You will earn a total of{' '}
                  <Text Text-14 Primary>
                    {formatAmount((1000 * Number(interestPotProductsTierRateMap?.[productId]?.[item.tier])) / 100, 'GBP', 2)}
                  </Text>{' '}
                  a year if you deposit £1000.
                </Text>
              </>)}
          </CardView>
        </TouchableBounce>), [
        colors.background.dark,
        handleContinue,
        interestPotProductsTierRateMap,
        productId,
        renderIcon,
        styles.flex1,
        styles.renderItem,
        styles.row,
        styles.tierContainer,
        styles.tierText,
    ]);
    const footer = useMemo(() => product?.interestPaymentFrequency ? (<Text TextThin-12 Secondary centered style={styles.footer}>
            *These projections are provided for illustrative purposes only and assumes you keep the account and the
            money in it for 12 months.
            {'\n\n'}
            Pots are FSCS eligible for up to £120,000 per bank, interest is paid{' '}
            {product?.interestPaymentFrequency?.toLocaleLowerCase()}.
          </Text>) : null, [product?.interestPaymentFrequency, styles.footer]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
        <AnimatedSectionList sections={sections} renderItem={renderItem} onScroll={scrollHandler} scrollEventThrottle={16} ListFooterComponent={footer} stickySectionHeadersEnabled={false} renderSectionHeader={renderSectionTitle} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}/>
      </AppView>);
}, {
    title: 'Interest rates',
});
const styleSet = createStyleSheets((colors) => ({
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex1: {
        flex: 1,
    },
    renderItem: {
        marginTop: rem(8),
        marginBottom: rem(8),
    },
    tierText: {
        textTransform: 'capitalize',
    },
    tierContainer: {
        borderRadius: rem(8),
        paddingVertical: rem(8),
        paddingHorizontal: rem(16),
        backgroundColor: colors.background.dark,
    },
    sectionHeaderContainer: {
        backgroundColor: colors.background.dark,
    },
    footer: {
        padding: rem(16),
    },
}));
export default PotInterestRatesScreen;
